import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { procurementApi } from 'api/procurement';
import { PurchaseOrderItemEditTable } from 'components/procurement/components/purchaseOrdersV2/entityView/PurchaseOrderItemEditTable';
import { PurchaseOrderItemLookup } from 'components/procurement/components/purchaseOrdersV2/formComponents/PurchaseOrderItemLookup';
import { useCountriesQuery } from 'components/procurement/components/purchaseOrdersV2/queries/AddressesQueries';
import {
    buildDisplayedItemErrorMessages,
    buildPurchaseOrderItemRowFromMaster,
    formatDuplicateLineItemLiveSummary,
    getDefaultCompanySourceIdsForPurchaseOrderItem,
    getEligibleCompanySourceIdsForPurchaseOrderItem,
    hasInvalidEmptyCompanySourceEligibility,
    reconcileSelectedCompanySourceIdsForPurchaseOrderItem,
    validateDuplicateLineItems,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderCreateAndEditUtils';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Label } from 'shadcn/components/ui/Label';
import { CompanySourcePicker } from 'shared/components/companySource';
import { useItemMasterQuery } from 'shared/hooks/warehouse/useItemMasterQuery';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * @param {Object} props
 * @param {Array<Object>} props.items
 * @param {(updater: any) => void} props.onItemsChange
 * @param {{current: {inputs?: Object, selects?: Object, multi?: Object}}} [props.itemEditStateRef]
 * @param {Set<string|number>} [props.removedItemIds]
 * @param {(updater: any) => void} [props.setRemovedItemIds]
 * @param {string|number|null} [props.vendorId]
 * @param {string} [props.scheduledDeliveryDate]
 * @param {boolean} [props.showValidation]
 * @param {Array<{id: string, sku?: string, messages: string[]}>} [props.itemErrors]
 * @param {string[]} [props.itemSummaryErrors]
 * @param {() => void} [props.onItemEditsChange]
 * @param {(params: { itemMaster: Object, nextRow: Object }) => void} [props.onItemAdded]
 */
export function PurchaseOrderCreateItemsCard({
    itemEditStateRef: itemEditStateRefProp,
    itemErrors = [],
    items,
    itemSummaryErrors = [],
    onItemAdded,
    onItemEditsChange,
    onItemsChange,
    removedItemIds: removedItemIdsProp,
    scheduledDeliveryDate = '',
    setRemovedItemIds: setRemovedItemIdsProp,
    showValidation = false,
    vendorId = null,
}) {
    const [selectedExternalSku, setSelectedExternalSku] = useState('');
    const [selectedItemMasterSku, setSelectedItemMasterSku] = useState('');
    const [selectedCompanySourceIds, setSelectedCompanySourceIds] = useState([]);
    const [isAddingItem, setIsAddingItem] = useState(false);
    const [addItemError, setAddItemError] = useState('');

    const internalItemEditStateRef = useRef({ inputs: {}, selects: {}, multi: {} });
    const [internalRemovedItemIds, setInternalRemovedItemIds] = useState(new Set());
    const nextIdRef = useRef(1);

    const itemEditStateRef = itemEditStateRefProp ?? internalItemEditStateRef;
    const removedItemIds = removedItemIdsProp ?? internalRemovedItemIds;
    const setRemovedItemIds = setRemovedItemIdsProp ?? setInternalRemovedItemIds;

    const { data: countries = [] } = useCountriesQuery({ enabled: true });
    const { data: itemMasters = [] } = useItemMasterQuery({ activeOnly: true, enabled: true, mmOnly: true });

    const itemMastersBySku = useMemo(() => {
        const map = new Map();
        for (const row of itemMasters) {
            map.set(String(row.sku), row);
        }
        return map;
    }, [itemMasters]);

    const selectedItemMaster = selectedItemMasterSku ? itemMastersBySku.get(String(selectedItemMasterSku)) : undefined;

    const eligibleSourceIds = useMemo(
        () => getEligibleCompanySourceIdsForPurchaseOrderItem(selectedItemMaster),
        [selectedItemMaster],
    );

    const hasInvalidCompanySourceEligibility = hasInvalidEmptyCompanySourceEligibility(selectedItemMaster);

    const effectiveCompanySourceIds = useMemo(() => {
        if (!selectedItemMaster) return selectedCompanySourceIds;
        return reconcileSelectedCompanySourceIdsForPurchaseOrderItem(selectedCompanySourceIds, selectedItemMaster);
    }, [selectedItemMaster, selectedCompanySourceIds]);

    const canAddItem =
        Boolean(selectedItemMasterSku) && effectiveCompanySourceIds.length > 0 && !hasInvalidCompanySourceEligibility;
    const canClearSelection = Boolean(selectedExternalSku || selectedItemMasterSku);
    const canSelectCompanySources = Boolean(selectedItemMasterSku);

    const handleItemMasterSkuChange = useCallback(
        (sku) => {
            setSelectedItemMasterSku(sku);
            setAddItemError('');
            const match = sku ? itemMastersBySku.get(String(sku)) : undefined;
            setSelectedCompanySourceIds(getDefaultCompanySourceIdsForPurchaseOrderItem(match));
        },
        [itemMastersBySku],
    );

    const handleCompanySourceIdsChange = useCallback((nextCompanySourceIds) => {
        setAddItemError('');
        setSelectedCompanySourceIds(nextCompanySourceIds);
    }, []);

    const syncAddItemDuplicateError = useCallback(() => {
        const activeRows = (Array.isArray(items) ? items : []).filter((row) => !removedItemIds?.has?.(String(row?.id)));
        const { itemSummaryErrors: duplicateSummaryErrors } = validateDuplicateLineItems({
            rows: activeRows,
            itemEditStateRef,
            itemMastersBySku,
            removedItemIds,
        });
        setAddItemError(formatDuplicateLineItemLiveSummary(duplicateSummaryErrors));
    }, [itemEditStateRef, itemMastersBySku, items, removedItemIds]);

    useEffect(() => {
        syncAddItemDuplicateError();
    }, [syncAddItemDuplicateError]);

    const handleItemEditsChangeWithDuplicateSync = useCallback(() => {
        syncAddItemDuplicateError();
        if (typeof onItemEditsChange === 'function') onItemEditsChange();
    }, [onItemEditsChange, syncAddItemDuplicateError]);

    const handleAddItem = useCallback(async () => {
        if (isAddingItem) return;
        if (!selectedItemMasterSku) return;
        const match = itemMastersBySku.get(String(selectedItemMasterSku));
        if (!match) return;
        if (hasInvalidEmptyCompanySourceEligibility(match)) return;

        if (!effectiveCompanySourceIds.length) return;

        const existingRows = (Array.isArray(items) ? items : []).filter(
            (row) => !removedItemIds?.has?.(String(row?.id)),
        );

        setIsAddingItem(true);
        let vendorSku = '';
        try {
            if (vendorId) {
                const resp = await procurementApi.getLastPOItemFromVendor(vendorId, match.sku);
                const last = resp?.status && resp?.data?.rows?.length ? resp.data.rows[0] : null;
                vendorSku = last?.vendorSku ?? '';
            }
        } catch (e) {
            // best-effort: ignore lookup failures and leave vendorSku blank
        } finally {
            setIsAddingItem(false);
        }

        let rowSequence = nextIdRef.current;
        const newRows = effectiveCompanySourceIds.map((companySourceId) => {
            const nextRow = buildPurchaseOrderItemRowFromMaster({
                match,
                companySourceId,
                vendorSku,
                scheduledDeliveryDate,
                rowSequence,
            });
            rowSequence += 1;
            return nextRow;
        });
        nextIdRef.current = rowSequence;

        onItemsChange((prev) => {
            const base = Array.isArray(prev) ? prev : [];
            return [...base, ...newRows];
        });

        if (typeof onItemAdded === 'function') {
            onItemAdded({ itemMaster: match, nextRow: newRows[0] });
        }

        const { itemSummaryErrors: duplicateSummaryErrors } = validateDuplicateLineItems({
            rows: [...existingRows, ...newRows],
            itemEditStateRef,
            itemMastersBySku,
            removedItemIds,
        });
        setAddItemError(formatDuplicateLineItemLiveSummary(duplicateSummaryErrors));

        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
        setSelectedCompanySourceIds([]);
    }, [
        isAddingItem,
        itemEditStateRef,
        itemMastersBySku,
        items,
        onItemAdded,
        onItemsChange,
        effectiveCompanySourceIds,
        removedItemIds,
        scheduledDeliveryDate,
        selectedExternalSku,
        selectedItemMasterSku,
        vendorId,
    ]);

    const handleRemoveRow = useCallback(
        (rowId) => {
            onItemsChange((prev) =>
                Array.isArray(prev) ? prev.filter((row) => String(row?.id) !== String(rowId)) : [],
            );

            if (itemEditStateRef?.current?.inputs) delete itemEditStateRef.current.inputs[rowId];
            if (itemEditStateRef?.current?.selects) delete itemEditStateRef.current.selects[rowId];
            if (itemEditStateRef?.current?.multi) delete itemEditStateRef.current.multi[rowId];
        },
        [itemEditStateRef, onItemsChange],
    );

    const handleClearSelections = useCallback(() => {
        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
        setSelectedCompanySourceIds([]);
        setAddItemError('');
    }, []);

    const showErrors = showValidation === true;
    const itemErrorMessages = useMemo(
        () =>
            buildDisplayedItemErrorMessages({
                addItemError,
                itemErrors,
                itemSummaryErrors,
                showValidation,
            }),
        [addItemError, itemErrors, itemSummaryErrors, showValidation],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Items in Purchase Order</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                <div className="flex flex-col gap-6">
                    {(showErrors || addItemError) && itemErrorMessages.length > 0 ? (
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following item issues to save</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {itemErrorMessages.map(({ key, message }) => (
                                        <li key={key}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <Label className={labelClassName}>
                                Look up an Item via an External Sku or Item Master Sku
                            </Label>
                        </div>

                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_minmax(200px,240px)_auto]">
                            <div className="min-w-0">
                                <PurchaseOrderItemLookup
                                    externalSku={selectedExternalSku}
                                    itemMasterSku={selectedItemMasterSku}
                                    onExternalSkuChange={setSelectedExternalSku}
                                    onItemMasterSkuChange={handleItemMasterSkuChange}
                                />
                            </div>
                            <div className="flex flex-col justify-end">
                                <CompanySourcePicker
                                    mode="multiple"
                                    value={effectiveCompanySourceIds}
                                    onValueChange={handleCompanySourceIdsChange}
                                    eligibleSourceIds={selectedItemMaster ? eligibleSourceIds : undefined}
                                    disabled={!canSelectCompanySources || hasInvalidCompanySourceEligibility}
                                />
                                {hasInvalidCompanySourceEligibility ? (
                                    <p className="mt-2 text-sm text-destructive">
                                        This item has no eligible company sources and cannot be added to a purchase
                                        order.
                                    </p>
                                ) : null}
                            </div>
                            <div className="flex flex-col justify-end">
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleClearSelections}
                                        disabled={!canClearSelection}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={handleAddItem}
                                        disabled={!canAddItem || isAddingItem}
                                    >
                                        Add Item
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {Array.isArray(items) && items.length > 0 ? (
                        <div className="overflow-x-auto">
                            <PurchaseOrderItemEditTable
                                rows={items}
                                countries={countries}
                                itemEditStateRef={itemEditStateRef}
                                removedItemIds={removedItemIds}
                                setRemovedItemIds={setRemovedItemIds}
                                onItemEditsChange={handleItemEditsChangeWithDuplicateSync}
                                onRemoveNewRow={handleRemoveRow}
                            />
                        </div>
                    ) : (
                        <div className="flex min-h-[160px] items-center justify-center rounded-md border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                            No items added yet. Add an item to proceed.
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

PurchaseOrderCreateItemsCard.displayName = 'PurchaseOrderCreateItemsCard';
